import React, { useEffect, useState } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { Kpi } from "./Kpi"
import { SyncFailureBanner } from "./SyncFailureBanner"
import { MrrMovementPanel } from "./MrrMovementPanel"
import { RevenueTrendChart } from "./RevenueTrendChart"
import { GrowthCard } from "./Sparkbars"
import { EfficiencyCard } from "./EfficiencyCard"
import { PlanBreakdown } from "./PlanBreakdown"
import { RevenueMix } from "./RevenueMix"
import { UninstallReasons } from "./UninstallReasons"
import { TopCustomers, TopSubscriptions, RecentTransactions } from "./DataList"
import { RecentActivityFeed } from "./RecentActivityFeed"
import { money, num, pct } from "./format"

// Picks the per-currency row for the primary (trend) currency, falling back to
// the first available currency.
const primaryRow = (revenue) => {
  const cur = revenue.trend_currency
  return revenue.by_currency.find((r) => r.currency === cur) || revenue.by_currency[0] || null
}

// Secondary currencies rendered as a small "· €3,200" suffix line.
const secondaryMoney = (revenue, field) => {
  const cur = revenue.trend_currency
  const rest = revenue.by_currency.filter((r) => r.currency !== cur)
  if (rest.length === 0) return null
  return rest.map((r) => money(r[field], r.currency)).join(" · ")
}

function Card({ title, subtitle, children, className = "" }) {
  return (
    <section className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 ${className}`}>
      {title && (
        <header className="mb-4">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </header>
      )}
      {children}
    </section>
  )
}

function Banner({ title, body, cta, onCta, tone = "info" }) {
  const ring = tone === "warn" ? "border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40" : "border-indigo-200 dark:border-indigo-900/50 bg-indigo-50 dark:bg-indigo-950/40"
  return (
    <div className={`rounded-xl border p-6 ${ring}`}>
      <p className="font-semibold text-gray-900 dark:text-gray-100">{title}</p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{body}</p>
      {cta && (
        <button
          type="button"
          onClick={onCta}
          className="mt-4 inline-flex rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all"
        >
          {cta}
        </button>
      )}
    </div>
  )
}

export default function HomeApp({ metricsPath, settingsPath, detailBasePath, copy }) {
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let active = true
    fetch(metricsPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => active && setMetrics(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [metricsPath])

  const header = (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
    </div>
  )

  const shell = (body) => (
    <ErrorBoundary>
      {header}
      <div className="mt-8">{body}</div>
    </ErrorBoundary>
  )

  if (loading) return shell(<p className="text-sm text-gray-400 dark:text-gray-500">…</p>)
  if (error || !metrics) return shell(<Banner title={copy.states.error} body="" tone="warn" />)

  if (metrics.sync_state === "needs_connection") {
    return shell(
      <Banner
        title={copy.states.needsConnectionTitle}
        body={copy.states.needsConnectionBody}
        cta={copy.states.needsConnectionCta}
        onCta={() => {
          window.location.href = settingsPath
        }}
      />
    )
  }

  const {
    revenue, headline, mrr_movement: movement, growth, efficiency, revenue_mix: revenueMix,
    merchants, subscriptions, top_customers: topCustomers, top_subscriptions: topSubscriptions,
    recent_transactions: recentTransactions, recent_events: recentEvents, sync_failure: syncFailure,
    uninstall_reasons: uninstallReasons,
  } = metrics

  const mrrRow = primaryRow(revenue)
  const mrrChange = headline.mrr_change_pct
  const merchantNet = merchants.installed_30d - merchants.churned_30d
  const subNet = headline.sub_adds_30d - headline.sub_churn_30d

  return shell(
    <div className="space-y-6">
      {syncFailure && <SyncFailureBanner failure={syncFailure} copy={copy.syncFailure} />}

      {metrics.sync_state === "backfilling" && (
        <Banner title={copy.states.backfillTitle} body={copy.states.backfillBody} tone="info" />
      )}

      {/* Headline KPI strip — 1/2/3/5 columns */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Kpi
          label={copy.metrics.mrr}
          value={mrrRow ? money(mrrRow.mrr_cents, mrrRow.currency) : money(0, "USD")}
          sub={secondaryMoney(revenue, "mrr_cents")}
          delta={`${mrrChange > 0 ? "↗ +" : "↘ "}${pct(mrrChange)} ${copy.metrics.vsLastMonth}`}
          deltaTone={mrrChange >= 0 ? "up" : "down"}
        />
        <Kpi
          label={copy.metrics.net30d}
          value={mrrRow ? money(mrrRow.net_30d_cents, mrrRow.currency) : money(0, "USD")}
          sub={secondaryMoney(revenue, "net_30d_cents")}
          footnote={copy.metrics.netOfFees}
        />
        <Kpi
          label={copy.metrics.earningsToDate}
          value={money(headline.earnings_to_date_cents, headline.currency)}
          footnote={copy.metrics.lifetime}
        />
        <Kpi
          label={copy.metrics.activeMerchants}
          value={num(merchants.active)}
          delta={`+${merchants.installed_30d} / -${merchants.churned_30d} ${copy.metrics.netAdds30d}`}
          deltaTone={merchantNet >= 0 ? "up" : "down"}
        />
        <Kpi
          label={copy.metrics.activeSubscriptions}
          value={num(subscriptions.active)}
          delta={`+${headline.sub_adds_30d} / -${headline.sub_churn_30d} ${copy.metrics.netSubs30d}`}
          deltaTone={subNet >= 0 ? "up" : "down"}
        />
      </div>

      {/* MRR movement + revenue trend */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={copy.sections.mrrMovement} subtitle={copy.sections.mrrMovementSubtitle}>
          <MrrMovementPanel movement={movement} copy={copy} />
        </Card>
        <Card title={copy.sections.revenueTrend} subtitle={copy.sections.revenueTrendSubtitle}>
          <RevenueTrendChart points={revenue.trend} />
        </Card>
      </div>

      {/* Growth strip + efficiency */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <GrowthCard label={copy.growth.customers} series={growth.customers} perWeekLabel={copy.growth.perWeek} />
        <GrowthCard label={copy.growth.subscriptions} series={growth.subscriptions} perWeekLabel={copy.growth.perWeek} />
        <GrowthCard label={copy.growth.transactions} series={growth.transactions} perWeekLabel={copy.growth.perWeek} />
        <EfficiencyCard efficiency={efficiency} copy={copy.growth} />
      </div>

      {/* Plan breakdown + revenue mix */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={copy.sections.planBreakdown} subtitle={copy.sections.planBreakdownSubtitle}>
          <PlanBreakdown
            plans={subscriptions.by_plan}
            emptyLabel={copy.states.empty}
            paidLabel={copy.sections.planBreakdownPaid}
            freeLabel={copy.sections.planBreakdownFree}
          />
        </Card>
        <Card title={copy.sections.revenueMix} subtitle={copy.sections.revenueMixSubtitle}>
          <RevenueMix mix={revenueMix} labels={copy.mix} emptyLabel={copy.states.empty} />
        </Card>
      </div>

      {/* Top customers + top subscriptions */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={copy.sections.topCustomers} subtitle={copy.sections.topCustomersSubtitle}>
          <TopCustomers rows={topCustomers} copy={copy.tables} emptyLabel={copy.states.empty} detailBasePath={detailBasePath} />
        </Card>
        <Card title={copy.sections.topSubscriptions} subtitle={copy.sections.topSubscriptionsSubtitle}>
          <TopSubscriptions rows={topSubscriptions} copy={copy.tables} emptyLabel={copy.states.empty} detailBasePath={detailBasePath} />
        </Card>
      </div>

      {/* Uninstall reasons — why merchants left */}
      <Card title={copy.sections.uninstallReasons} subtitle={copy.sections.uninstallReasonsSubtitle}>
        <UninstallReasons data={uninstallReasons} copy={copy.uninstallReasons} emptyLabel={copy.states.empty} />
      </Card>

      {/* Recent transactions + recent activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={copy.sections.recentTransactions} subtitle={copy.sections.recentTransactionsSubtitle}>
          <RecentTransactions rows={recentTransactions} emptyLabel={copy.states.empty} detailBasePath={detailBasePath} />
        </Card>
        <Card title={copy.sections.recentActivity} subtitle={copy.sections.recentActivitySubtitle}>
          <RecentActivityFeed events={recentEvents} labels={copy.events} emptyLabel={copy.states.empty} detailBasePath={detailBasePath} />
        </Card>
      </div>
    </div>
  )
}
